import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppBackButton } from '../components/AppBackButton';

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-surface dark:bg-slate-950 text-ink dark:text-slate-200 p-6 md:p-12 font-sans selection:bg-primary/30">
      <div className="max-w-3xl mx-auto space-y-10">
        
        <AppBackButton fallbackTo="/" className="mb-8" />

        <header className="space-y-4 border-b border-black/[0.06] dark:border-white/[0.06] pb-8">
          <h1 className="text-3xl md:text-4xl font-medium tracking-[-0.03em]">POLÍTICA DE PRIVACIDADE / PRIVACY POLICY</h1>
          <p className="text-sm font-medium text-slate-500">Última atualização / Last updated: 1 de junho de 2026</p>
        </header>

        <div className="prose prose-slate dark:prose-invert prose-p:leading-relaxed prose-headings:font-medium max-w-none">
          <h2>VERSÃO EM PORTUGUÊS</h2>
          <p>
            A sua privacidade é de extrema importância para nós. Esta Política de Privacidade descreve como o LibriTui coleta, utiliza, armazena e protege as suas informações ao utilizar o nosso aplicativo e serviços de idiomas.
          </p>

          <h3>1. Informações que Coletamos</h3>
          <p>
            <strong>Dados de Cadastro:</strong> Coletamos informações fornecidas voluntariamente por você ao criar uma conta, tais como nome, endereço de e-mail e preferências de idioma nativo e de estudo.
          </p>
          <p>
            <strong>Dados de Progresso e Uso:</strong> Para fornecer uma experiência personalizada, armazenamos dados relacionados ao seu histórico de leitura, livros salvos na estante, flashcards criados, notas de estudo e estatísticas de progresso.
          </p>
          <p>
            <strong>Conteúdo da Comunidade:</strong> Armazenamos as postagens, reações, curtidas e comentários que você decide compartilhar publicamente na aba de feed da comunidade.
          </p>

          <h3>2. Uso dos Dados e Cache Local</h3>
          <p>
            <strong>Otimização de Performance:</strong> O LibriTui utiliza um sistema híbrido de armazenamento. Suas preferências, estados visuais e logs recentes de navegação podem ser salvos em cache local no seu dispositivo para garantir carregamento instantâneo e funcionamento offline de recursos específicos.
          </p>
          <p>
            <strong>Sincronização na Nuvem:</strong> Os dados de perfil, progresso e postagens são sincronizados de forma segura e armazenados em nossa infraestrutura de nuvem baseada no Cloudflare D1.
          </p>
          <p>
            <strong>Melhoria do Serviço:</strong> Não vendemos seus dados pessoais a terceiros. As informações de uso são coletadas estritamente para manter o aplicativo funcionando e aprimorar os algoritmos de ensino.
          </p>

          <h3>3. Compartilhamento de Dados com Serviços de IA</h3>
          <p>
            Ao solicitar traduções avançadas ou utilizar a função de chat de conversação, trechos de textos ou mensagens podem ser processados por APIs de inteligência artificial de parceiros tecnológicos terceiros (como OpenAI ou Google). Esses dados são transmitidos de forma anônima ou sob tokens de sessão, sendo proibido o uso destes dados pelos provedores para finalidades externas que não sejam responder à requisição do próprio aplicativo.
          </p>

          <h3>4. Segurança da Informação</h3>
          <p>
            Implementamos medidas técnicas e organizacionais adequadas para proteger seus dados pessoais contra acesso não autorizado, alteração, divulgação ou destruição. No entanto, nenhum sistema de transmissão de dados pela internet ou armazenamento digital é 100% seguro, e não podemos garantir segurança absoluta.
          </p>

          <h3>5. Direitos do Usuário (LGPD / GDPR)</h3>
          <p>
            Você possui total controle sobre as suas informações. A qualquer momento, através das configurações do aplicativo ou entrando em contato com o suporte, você tem o direito de:
          </p>
          <ul>
            <li>Acessar, corrigir ou atualizar seus dados cadastrais.</li>
            <li>Solicitar a exclusão definitiva de sua conta e de todos os dados associados a ela de nossos bancos de dados na nuvem.</li>
          </ul>

          <h3>6. Contato e Suporte</h3>
          <p>
            Se você tiver dúvidas, sugestões ou precisar solicitar a exclusão de seus dados e suporte técnico relacionado à sua conta no LibriTui, entre em contato diretamente através do e-mail oficial: libritui@gmail.com.
          </p>

          <hr className="my-12 border-black/[0.06] dark:border-white/[0.06]" />

          <h2>ENGLISH VERSION</h2>
          <p>
            Your privacy is of utmost importance to us. This Privacy Policy describes how LibriTui collects, uses, stores, and protects your information when you use our application and language services.
          </p>

          <h3>1. Information We Collect</h3>
          <p>
            <strong>Registration Data:</strong> We collect information voluntarily provided by you when creating an account, such as your name, email address, and native and study language preferences.
          </p>
          <p>
            <strong>Progress and Usage Data:</strong> To provide a personalized experience, we store data related to your reading history, books saved on your shelf, created flashcards, study notes, and progress statistics.
          </p>
          <p>
            <strong>Community Content:</strong> We store the posts, reactions, likes, and comments that you choose to share publicly in the community feed tab.
          </p>

          <h3>2. Data Usage and Local Cache</h3>
          <p>
            <strong>Performance Optimization:</strong> LibriTui utilizes a hybrid storage system. Your preferences, visual states, and recent navigation logs may be saved in a local cache on your device to ensure instant loading and offline functionality for specific features.
          </p>
          <p>
            <strong>Cloud Synchronization:</strong> Profile, progress, and post data are securely synchronized and stored in our cloud infrastructure powered by Cloudflare D1.
          </p>
          <p>
            <strong>Service Improvement:</strong> We do not sell your personal data to third parties. Usage information is collected strictly to maintain application functionality and improve language learning algorithms.
          </p>

          <h3>3. Data Sharing with AI Services</h3>
          <p>
            When requesting advanced translations or using the conversation chat feature, text snippets or messages may be processed by third-party technological partner artificial intelligence APIs (such as OpenAI or Google). This data is transmitted anonymously or under session tokens, and providers are prohibited from using this data for external purposes other than responding to the application's request.
          </p>

          <h3>4. Information Security</h3>
          <p>
            We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmitting data over the internet or digital storage is 100% secure, and we cannot guarantee absolute security.
          </p>

          <h3>5. User Rights (LGPD / GDPR compliance)</h3>
          <p>
            You have full control over your information. At any time, through the application settings or by contacting support, you have the right to:
          </p>
          <ul>
            <li>Access, correct, or update your registration data.</li>
            <li>Request the permanent deletion of your account and all associated data from our cloud databases.</li>
          </ul>

          <h3>6. Contact and Support</h3>
          <p>
            If you have questions, suggestions, or need to request the deletion of your data or technical support related to your LibriTui account, please contact us directly via our official email: libritui@gmail.com.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
